/**
 * Cliente da API do backend — Nexora Grid (frontend).
 *
 * Criado em: 16/10/2026 · Última modificação: 26/10/2026 · Versão: 1.4.0
 *
 * Todas as chamadas vão para `/api` (proxy do Vite em desenvolvimento; proxy HTTPS em produção) com o token da
 * plataforma no cabeçalho. 401 dispara `nexora:sessao-expirada` (a tela volta ao login). O canal de tempo real
 * (GUS-1104) é lido com `fetch` em fluxo: o EventSource do navegador não envia cabeçalho.
 * v1.1: `baixar()` para arquivos autenticados (CSV do relatório diário — US-304).
 * v1.4 (US-3202): `fluxo()` — POST com resposta em SSE (o chat mostra o status e escreve a resposta aos poucos).
 */

export type Usuario = {
  id: string; login: string; nome: string; perfil: "cliente" | "suporte" | "integrador"; empresa: string | null;
  empresas: string[]; papeis: string[];
  /** US-1801: capacidades da matriz central do backend (`backend/permissoes.py`). */
  capacidades?: string[];
  funcoes_treinamento?: string[];
  perfis_validacao?: string[];
  nomes_empresas?: Record<string, string>;  // operadora → nome (barra superior)
  trocar_senha?: boolean;  // senha provisória: só a tela de troca (`paginas/TrocarSenha.tsx`)
  modo_exibicao?: "escuro" | "claro" | "contraste";  // US-4507: escolha do usuário
};
export type Sessao = { token: string; usuario: Usuario; expiraEm: string };

const CHAVE = "nexora.sessao";

export class ErroApi extends Error {
  constructor(public status: number, public detalhe: unknown) {
    super(typeof detalhe === "string" ? detalhe : Array.isArray(detalhe)
      ? detalhe.map((d) => (d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg)
        : JSON.stringify(d))).join("; ") : `HTTP ${status}`);
  }
}

/** Sessão guardada nesta aba (some ao fechar a aba). */
export function lerSessao(): Sessao | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    const s = bruto ? (JSON.parse(bruto) as Sessao) : null;
    return s && new Date(s.expiraEm).getTime() > Date.now() ? s : null;
  } catch {
    return null;
  }
}

export function guardarSessao(s: Sessao | null): void {
  try {
    if (s) sessionStorage.setItem(CHAVE, JSON.stringify(s)); else sessionStorage.removeItem(CHAVE);
  } catch {
    /* sem armazenamento: a sessão vale até recarregar */
  }
}

let tokenAtual: string | null = lerSessao()?.token ?? null;
export function definirToken(token: string | null): void { tokenAtual = token; }

type Opcoes = { metodo?: string; corpo?: unknown; form?: FormData; sinal?: AbortSignal };

/** Chamada JSON (ou multipart com `form`) ao backend. */
export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const cabecalhos: Record<string, string> = {};
  if (tokenAtual) cabecalhos.Authorization = `Bearer ${tokenAtual}`;
  if (opcoes.corpo !== undefined) cabecalhos["Content-Type"] = "application/json";
  const resposta = await fetch(`/api${caminho}`, {
    method: opcoes.metodo ?? (opcoes.corpo !== undefined || opcoes.form ? "POST" : "GET"), headers: cabecalhos,
    body: opcoes.form ?? (opcoes.corpo !== undefined ? JSON.stringify(opcoes.corpo) : undefined), signal: opcoes.sinal,
  });
  if (resposta.status === 401 && tokenAtual) window.dispatchEvent(new Event("nexora:sessao-expirada"));
  if (resposta.status === 204) return undefined as T;
  const corpo = await resposta.json().catch(() => null);
  if (!resposta.ok) throw new ErroApi(resposta.status, corpo?.detail ?? corpo);
  return corpo as T;
}

/** US-3202: POST cuja resposta é SSE (`event:`/`data:`); chama `aoEvento` a cada evento. Erro HTTP vira ErroApi. */
export async function fluxo(caminho: string, corpo: unknown, aoEvento: (evento: string, dados: any) => void, sinal?: AbortSignal): Promise<void> {
  const cabecalhos: Record<string, string> = { "Content-Type": "application/json", Accept: "text/event-stream" };
  if (tokenAtual) cabecalhos.Authorization = `Bearer ${tokenAtual}`;
  const resposta = await fetch(`/api${caminho}`, { method: "POST", headers: cabecalhos, body: JSON.stringify(corpo), signal: sinal });
  if (resposta.status === 401 && tokenAtual) window.dispatchEvent(new Event("nexora:sessao-expirada"));
  if (!resposta.ok || !resposta.body) {
    const erro = await resposta.json().catch(() => null);
    throw new ErroApi(resposta.status, erro?.detail ?? erro);
  }
  const leitor = resposta.body.getReader();
  const decodificador = new TextDecoder();
  let resto = "";
  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    resto += decodificador.decode(value, { stream: true });
    let fim: number;
    while ((fim = resto.indexOf("\n\n")) >= 0) {
      const bloco = resto.slice(0, fim);
      resto = resto.slice(fim + 2);
      let evento = "message";
      const dados: string[] = [];
      for (const linha of bloco.split("\n")) {
        if (linha.startsWith("event:")) evento = linha.slice(6).trim();
        else if (linha.startsWith("data:")) dados.push(linha.slice(5).trim());
      }
      if (dados.length) aoEvento(evento, JSON.parse(dados.join("\n")));
    }
  }
}

/** Baixa um arquivo autenticado (ex.: CSV do relatório — US-304) e oferece ao navegador com o nome dado. */
export async function baixar(caminho: string, nomeArquivo: string): Promise<void> {
  const resposta = await fetch(`/api${caminho}`, { headers: tokenAtual ? { Authorization: `Bearer ${tokenAtual}` } : {} });
  if (!resposta.ok) throw new ErroApi(resposta.status, `HTTP ${resposta.status}`);
  const url = URL.createObjectURL(await resposta.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export type EventoSSE = { evento: string; dados: unknown };

/** Lê um fluxo SSE autenticado (GUS-1104) e entrega cada evento; termina quando o servidor fecha ou `sinal` aborta. */
export async function lerFluxo(caminho: string, aoEvento: (e: EventoSSE) => void, sinal: AbortSignal): Promise<void> {
  const resposta = await fetch(`/api${caminho}`, {
    headers: tokenAtual ? { Authorization: `Bearer ${tokenAtual}`, Accept: "text/event-stream" } : {}, signal: sinal,
  });
  if (resposta.status === 401) window.dispatchEvent(new Event("nexora:sessao-expirada"));
  if (!resposta.ok || !resposta.body) throw new ErroApi(resposta.status, (await resposta.json().catch(() => null))?.detail);
  const leitor = resposta.body.pipeThrough(new TextDecoderStream()).getReader();
  let resto = "";
  for (;;) {
    const { value, done } = await leitor.read();
    if (done) return;
    resto += value;
    let fim: number;
    while ((fim = resto.indexOf("\n\n")) >= 0) {
      const bloco = resto.slice(0, fim);
      resto = resto.slice(fim + 2);
      const evento = /^event: (.*)$/m.exec(bloco)?.[1];
      const dados = /^data: (.*)$/m.exec(bloco)?.[1];
      if (evento && dados) aoEvento({ evento, dados: JSON.parse(dados) });
    }
  }
}
